import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { getUrlToBackendApi, request } from '@services/api';

import { useQueryGetMe } from '@hooks/api/user/use-query-me';

import { QuestType } from '@types';

const QUESTS_REQUEST_URL = '/quests';
const MY_QUESTS_REQUEST_URL = '/my_quests';

export const GET_QUESTS_QUERY_KEY = ['quests', 'list'];

type QuestsResponseType = {
	status: string;
	data: QuestType[];
	meta: {
		categories: string[];
	};
	pagination: {
		current_page: number;
		next_page: number | null;
		prev_page: number | null;
		total_pages: number;
		total_count: number;
		limit_value: number;
	};
};

export const useQueryQuests = () => {
	const { data: userData } = useQueryGetMe();
	const isAuthenticated = !!userData?.pm_id;

	const endpoint = isAuthenticated ? MY_QUESTS_REQUEST_URL : QUESTS_REQUEST_URL;

	return useQuery<unknown, unknown, QuestsResponseType>({
		queryKey: [...GET_QUESTS_QUERY_KEY, isAuthenticated ? 'my' : 'public'],
		queryFn: () => {
			return request<QuestsResponseType>({
				method: 'get',
				url: getUrlToBackendApi(endpoint).href
			});
		},
		retry: false,
		enabled: !!userData // Wait for user data to be loaded
	});
};

type Params = {
	[key: string]: string | number | null | boolean | undefined | Array<string | number>;
};

const baseParams = {
	page: 1,
	per_page: 8
};

export const useQueryQuestsWithParams = (params: Params) => {
	const { data: userData } = useQueryGetMe();
	const isAuthenticated = !!userData?.pm_id;

	const endpoint = isAuthenticated ? MY_QUESTS_REQUEST_URL : QUESTS_REQUEST_URL;

	const stringifiedParams = JSON.stringify(params);

	return useInfiniteQuery<QuestsResponseType>({
		queryKey: [...GET_QUESTS_QUERY_KEY, 'paginated', isAuthenticated ? 'my' : 'public', stringifiedParams],
		queryFn: ({ pageParam }) => {
			const normalizedParams = { ...baseParams, page: pageParam, ...params };

			const url = getUrlToBackendApi(endpoint);

			if (normalizedParams) {
				Object.entries(normalizedParams).forEach(([key, value]) => {
					if (value == null) return;

					if (Array.isArray(value)) {
						value.forEach(v => {
							url.searchParams.append(key, String(v));
						});
					} else {
						url.searchParams.append(key, String(value));
					}
				});
			}

			return request<QuestsResponseType>({
				method: 'get',
				url: url.href
			});
		},
		initialPageParam: 1,
		getNextPageParam: ({ pagination }) => pagination.next_page,
		retry: false,
		enabled: !!userData // Wait for user data to be loaded
	});
};
